import type { Rule } from '../api'

/**
 * What a rule cites, said so somebody can go and look.
 *
 * The screens showed `cites C15`. `C15` is a cell of a spreadsheet, and a cell
 * reference names nothing at all unless the reader is told which spreadsheet —
 * so the most precise provenance in the store was rendered as the least useful
 * thing on the row. A clause reference has the same problem in a milder form:
 * `Sch. 2 para 4(b)` is a schedule of *something*.
 *
 * So the document is named, the wording says which kind of reference it is, and
 * where the appliance actually holds the file the whole thing is a link. §5.5's
 * point is that a citation can be followed; a citation that cannot be is a
 * string.
 */
export function Cited({ rule }: { rule: Rule }) {
  if (!rule.source_document_id) return null

  const title = rule.source_document_title
  const isPack = rule.source_document_kind === 'pack'
  const where = rule.source_clause
    ? isPack
      ? `cell ${rule.source_clause}`
      : rule.source_clause
    : null

  // Only a pack is a file this appliance is holding. Everything else is a
  // reference into the customer's own document system, and a link that 404s
  // teaches the reader to stop clicking the ones that work.
  const body = (
    <>
      {where ? `${where} of ` : 'cited to '}
      {title ?? 'a document'}
    </>
  )

  return isPack ? (
    <a
      className="cited"
      href={`/api/documents/${rule.source_document_id}/content`}
      title="Opens the workbook this was read out of"
    >
      {body}
    </a>
  ) : (
    <span className="cited">{body}</span>
  )
}
